import React, { useContext, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import {
  Onboarding,
  OnboardingContext,
  OnboardingTitle,
} from 'registration/containers/Onboarding';
import { TechOnboardingOptions } from 'registration/types';

import { Checkbox } from 'shared/components/ihcl/checkbox';
import { Input } from 'shared/components/ihcl/input';
import { LucideIcon } from 'shared/components/ihcl/lucideIcon';
import { MatchingTextHighlight, Stack } from 'shared/components/ihcl/patterns';
import { Center } from 'shared/components/ihcl/positioning';
import { styled } from 'shared/components/ihcl/styled';
import { KIND, Tag } from 'shared/components/ihcl/tag';
import { Text } from 'shared/components/ihcl/text';
import { Button } from 'shared/components/ihcl/v2/button';
import simplePluralize from 'shared/helpers/simplePluralize';

const RoleContent = styled('div', () => ({
  textAlign: 'left',
}));

const RelatedRolesWrapper = styled(Stack, ({ $theme }) => ({
  paddingTop: $theme.sizing.unit16,
}));

const RelatedRolesTitleWrapper = styled('div', ({ $theme }) => ({
  textAlign: 'left',
  paddingTop: $theme.sizing.unit8,
  paddingBottom: $theme.sizing.unit8,
}));

const Divider = styled('hr', ({ $theme }) => ({
  borderColor: $theme.colors.grayLine,
  width: '100%',
  height: '1px',
  margin: '0',
}));

const SelectedTags = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: $theme.sizing.unit16,
  alignSelf: 'stretch',
  width: '100%',
}));

const SelectedTagsWrapper = styled('div', () => ({
  width: '256px',
}));

const OnboardingTechRolesFiltered = ({
  stepNumber,
}: OnboardingTechRolesProps) => {
  const {
    isLiteProfileActivating,
    onboardingData,
    onboardingOptions,
    setOnboardingData,
  } = useContext(OnboardingContext);
  const { techRoleOptions } = onboardingData;
  const [searchQuery, setSearchQuery] = useState('');
  const [isSelectedRolesExpanded, setIsSelectedRolesExpanded] = useState(false);

  useEffect(() => {
    if (isLiteProfileActivating && !onboardingData.hiring_activated) {
      setOnboardingData((data) => ({ ...data, hiring_activated: true }));
    }
  }, [
    isLiteProfileActivating,
    onboardingData.hiring_activated,
    setOnboardingData,
  ]);

  const stepKeys = ['techRoleOptions', 'hiring_activated'];

  const { tech_job_descriptions: techJobDescriptions } =
    onboardingOptions as TechOnboardingOptions;

  const unfilteredTechRoleOptions = techJobDescriptions.map(
    ({ category, id, title }) => ({
      category,
      title,
      id,
    })
  );

  const selectedRoleIds = (onboardingData?.techRoleOptions || []).map(
    (option) => option.id
  );

  const filteredTechRoleOptions = unfilteredTechRoleOptions.filter((option) =>
    option.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get categories from search results
  const searchResultCategories = new Set(
    filteredTechRoleOptions.map((role) => role.category)
  );

  // Get related roles (roles with same category as search results)
  const relatedRoles = searchQuery
    ? unfilteredTechRoleOptions.filter(
        (role) =>
          searchResultCategories.has(role.category) &&
          !filteredTechRoleOptions.some(
            (searchRole) => searchRole.id === role.id
          )
      )
    : [];

  const handleRoleToggle = (role: (typeof techRoleOptions)[number]) => {
    const isSelected = selectedRoleIds.includes(role.id);
    if (isSelected) {
      const newTechRoleOptions = techRoleOptions.filter(
        (option) => option.id !== role.id
      );
      setOnboardingData({
        ...onboardingData,
        techRoleOptions: newTechRoleOptions,
      });
    } else {
      setOnboardingData({
        ...onboardingData,
        techRoleOptions: [...(techRoleOptions || []), role],
      });
    }
  };

  const renderRoleItem = (role: (typeof techRoleOptions)[number]) => (
    <Checkbox
      key={`${role.id}-${role.title}`}
      checked={selectedRoleIds.includes(role.id)}
      onChange={() => handleRoleToggle(role)}
      ariaLabel={role.title}
    >
      <RoleContent>
        <Text variant="ParagraphMedium">
          <MatchingTextHighlight text={role.title} searchTerm={searchQuery} />
        </Text>
      </RoleContent>
    </Checkbox>
  );

  return (
    <Center>
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0, interactive-widget=overlays-content"
        />
      </Helmet>
      <Onboarding
        autoSpacedStepContents={false}
        centerStepContents
        isNextDisabled={!techRoleOptions || techRoleOptions?.length === 0}
        currentStep={stepNumber}
        stepKeys={stepKeys}
        stepName="Tech roles"
      >
        <Stack $gapSizing="unit16">
          <OnboardingTitle $noBottomMargin>
            Select tech roles you have experience in!
          </OnboardingTitle>
          <>
            <Button
              variant="minimal"
              disabled={!techRoleOptions || techRoleOptions?.length < 1}
              color="alternate"
              onClick={() =>
                setIsSelectedRolesExpanded(!isSelectedRolesExpanded)
              }
            >
              {simplePluralize(
                techRoleOptions?.length || 0,
                'selected role',
                'selected roles'
              )}
              <LucideIcon
                name={isSelectedRolesExpanded ? 'ChevronUp' : 'ChevronDown'}
                size="unit16"
              />
            </Button>
            {isSelectedRolesExpanded && (
              <SelectedTags>
                <SelectedTagsWrapper>
                  {techRoleOptions?.map((option) => (
                    <Tag
                      key={option.id}
                      kind={KIND.custom}
                      size="medium"
                      color="accent00"
                      $textColor="accent60"
                      variant="solid"
                      $fadeIn
                      $containerWidth
                      onActionClick={() => handleRoleToggle(option)}
                    >
                      <Text variant="LabelSmall" color="accent60">
                        {option.title}
                      </Text>
                    </Tag>
                  ))}
                </SelectedTagsWrapper>
              </SelectedTags>
            )}
          </>
          <Divider />
          <Input
            startEnhancer={<LucideIcon name="Search" size="unit16" />}
            placeholder="Search all tech roles"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="tech-roles-search-input"
          />
          <Stack $flexDirection="column" $gapSizing="unit10">
            {filteredTechRoleOptions.map(renderRoleItem)}
            {searchQuery && relatedRoles.length > 0 && (
              <RelatedRolesWrapper>
                <Stack $flexDirection="column" $gapSizing="unit10">
                  <Divider />
                  <RelatedRolesTitleWrapper>
                    <Text variant="LabelSmallBold" color="grayLabel">
                      Related roles
                    </Text>
                  </RelatedRolesTitleWrapper>
                  {relatedRoles.map(renderRoleItem)}
                </Stack>
              </RelatedRolesWrapper>
            )}
          </Stack>
        </Stack>
      </Onboarding>
    </Center>
  );
};

type OnboardingTechRolesProps = {
  stepNumber: number;
};

export default OnboardingTechRolesFiltered;
